import { FC, useState } from "react"
import { Formik } from "formik"
import { ResponseObject } from "@coherentglobal/wasm-runner/dist/types"
import { InputJson } from "./InputJson"
import { formatFormValues, formatRequestData, prepareEditableJsonData } from "../../utils/dataFormat"
import { Tab, Tabs, TabPanel, TabList } from "react-tabs"
import { CoherentMiniIcon } from "../../icons/CoherentMiniIcon"
import { OutputDisplay } from "../output/OutputDisplay"
import { FormIcon } from "../../icons/FormIcon"
import { KeyboardIcon } from "../../icons/KeyboardIcon"
import { ExcelIcon } from "../../icons/ExcelIcon"
import { IntegrateSection } from "./IntegrateSection"
import { FormValues } from "../../types"
import { DashboardForm } from "./DashboardForm"
import { WasmRunner } from "@coherentglobal/wasm-runner"

export type DashboardPageProps = {
  /**
   * Existing request data used to repopulate the form after switching tabs
   */
  request?: any
  /**
   * Response data used to populate .json view
   */
  response?: any
  /**
   * Save the request so it can be used in the output tab
   */
  setRequest: (value: any) => void
  /**
   * Save the response so it can be used in the output tab
   */
  setResponse: (value: ResponseObject | { error: any }) => void
  /**
   * Wasm runner initializer function
   */
  initWasmRunner: () => void
  /**
   * Wasm runner
   */
  runner: WasmRunner
}

export const DashboardPage: FC<DashboardPageProps> = ({
  request = null,
  response = null,
  setRequest,
  setResponse,
  initWasmRunner,
  runner,
}) => {
  const [jsonView, setJsonView] = useState<boolean>(false)
  const [editableModelData, setEditableModelData] = useState<any>(prepareEditableJsonData())
  const [tabIndex, setTabIndex] = useState<number>(0)

  const runCalculation = async (payload: any) => {
    if (runner) {
      return await runner.execute(payload).catch((err) => ({ error: err.v3 }))
    } else {
      await initWasmRunner()
      console.error("err")

      throw Error("Runner not initialized. Please retry.")
    }
  }

  const calculate = async (values: any) => {
    const response = await runCalculation(formatRequestData(values))
    setRequest(values)
    setResponse(response)
  }

  const calculateJSON = async (values: any) => {
    const response = await runCalculation(values)
    setRequest(values)
    setResponse(response)
  }

  return (
    <div className="w-full lg:max-w-5xl mx-auto mt-8 mb-4 flex justify-between">
      <div className="h-full w-1/3 flex flex-col space-y-6 pr-20 justify-start border-r">
        <h1 className="pt-8 text-left text-black text-6xl">
          <b>
            401K
            <br />
            Calculator
          </b>
          <br />
          App
        </h1>
        <p>
          Coherent Spark instantly converts Excel into APIs so you can control, connect and comply across business
          logic.
        </p>
        <a
          href="https://coherent.global/"
          target="_blank"
          rel="noreferrer"
          className="bg-brand-light text-white text-tab font-semibold px-3 rounded h-10 leading-10 text-center"
        >
          LEARN MORE
        </a>
      </div>

      <Tabs
        className="relative space-y-6 max-w-xl w-full font-semibold text-tab"
        selectedIndex={tabIndex}
        onSelect={(index) => setTabIndex(index)}
      >
        <TabList className="w-full flex bg-white/30 text-brand-dark/30">
          <Tab
            className="w-1/2 h-16 rounded-l-lg px-6 transition flex items-center  cursor-pointer"
            selectedClassName="bg-white text-black"
            disabledClassName="cursor-not-allowed"
          >
            <div className="rounded-full bg-violet-100 p-2 mr-1">
              <FormIcon className="w-5 h-5" />
            </div>
            FORM
          </Tab>

          <Tab
            className="w-1/2 h-16 rounded-r-lg px-6 transition flex items-center cursor-pointer"
            selectedClassName="bg-white text-black"
            disabledClassName="cursor-not-allowed"
            disabled={!response}
          >
            <div className="rounded-full bg-violet-100 p-2 mr-1">
              <KeyboardIcon />
            </div>
            CALCULATION
          </Tab>
        </TabList>

        <div className="flex mx-auto w-full max-w-sm">
          <button
            className={`${
              !jsonView ? "border-black bg-black text-white" : "border-slate-300 text-slate-300"
            } border py-1 h-10 text-tab w-1/2 rounded-full transition`}
            type="button"
            onClick={() => setJsonView(false)}
          >
            FORM FIELDS
          </button>

          <button
            className={`${
              jsonView ? "border-black bg-black text-white" : "border-slate-300 text-slate-300"
            } border py-1 ml-1 h-10 text-tab w-1/2 rounded-full transition`}
            type="button"
            onClick={() => setJsonView(true)}
          >
            FOR DEVELOPERS
          </button>
        </div>

        <div
          className={`relative z-10 w-full mx-auto rounded-lg overflow-auto ${jsonView ? "bg-[#EFEFEF]" : "bg-white"}`}
        >
          <TabPanel className="p-0 tall:max-h-full" selectedClassName="!p-8">
            <Formik
              initialValues={request ? request : editableModelData.request_data.inputs}
              onSubmit={(values: FormValues) => {
                const formattedValues = formatFormValues(values)
                calculate(formattedValues)
                setTabIndex(1)
              }}
            >
              {({ errors, touched, values, handleChange, setFieldValue, resetForm, handleBlur }) => (
                <>
                  {jsonView ? (
                    <>
                      <div className="w-full bg-white rounded-lg p-4 h-[276px] flex flex-col space-y-4">
                        <div className="rounded-full text-center p-1 bg-[#F3E7FF] text-brand-light w-6">1</div>
                        <span className="font-bold">Upload your spreadsheets to Spark Coherent</span>
                        <div className="bg-form-medium h-full flex items-center justify-center">
                          <ExcelIcon className="text-brand-dark" />
                        </div>
                      </div>

                      <div className="bg-white rounded-lg p-4 h-[276px] w-full flex flex-col space-y-4 my-4">
                        <div className="rounded-full text-center p-1 bg-[#F3E7FF] text-brand-light w-6">2</div>
                        <span className="font-bold">Convert your spreadsheets into ready-to-integrate APIs</span>
                        <InputJson
                          calculate={(e) => {
                            calculateJSON(e)
                            setTabIndex(1)
                          }}
                          defaultValues={editableModelData}
                          setDefaultFormValues={setEditableModelData}
                          resetDefaultValues={() => setEditableModelData(prepareEditableJsonData())}
                          height="100%"
                        />
                      </div>
                      <div className="bg-white rounded-lg p-4 w-full">
                        <div className="rounded-full text-center p-1 bg-[#F3E7FF] text-brand-light w-6">3</div>
                        <div className="font-bold my-2">Integrate with virtually any platform on the market</div>
                        <IntegrateSection />
                      </div>
                    </>
                  ) : (
                    <DashboardForm
                      responseData={response ? response.response_data : null}
                      handleBlur={handleBlur}
                      errors={errors}
                      touched={touched}
                      values={values}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                      resetForm={resetForm}
                    />
                  )}
                </>
              )}
            </Formik>
          </TabPanel>
          <TabPanel className="p-0 tall:max-h-full" selectedClassName="!p-8">
            {jsonView ? (
              <>
                <div className="bg-white rounded-lg p-4 h-[276px] w-full flex flex-col space-y-4">
                  <div className="rounded-full text-center p-1 bg-[#F3E7FF] text-brand-light w-6">1</div>
                  <span className="font-bold">Upload your spreadsheets to Spark Coherent</span>
                  <div className="bg-form-medium h-full flex items-center justify-center">
                    <ExcelIcon className="text-brand-dark" />
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 h-[276px] w-full flex flex-col space-y-4 my-4">
                  <div className="rounded-full text-center p-1 bg-[#F3E7FF] text-brand-light w-6">2</div>
                  <span className="font-bold">Convert your spreadsheets into ready-to-integrate APIs</span>
                  <InputJson defaultValues={response} height="100%" />
                </div>
                <div className="bg-white rounded-lg p-4 w-full">
                  <div className="rounded-full text-center p-1 bg-[#F3E7FF] text-brand-light w-6">3</div>
                  <div className="font-bold my-2">Integrate with virtually any platform on the market</div>
                  <IntegrateSection />
                </div>
              </>
            ) : (
              <OutputDisplay
                response={response}
                resetFunction={() => {
                  setTabIndex(0)
                  setRequest(null)
                }}
              />
            )}
          </TabPanel>
        </div>
        <div className="w-full flex justify-center !mt-10">
          <CoherentMiniIcon className="text-[#D1D1D1]" />
        </div>
      </Tabs>
    </div>
  )
}

